import React, { useState, useCallback, useEffect } from 'react';
import {
  Car,
  Truck,
  Bike,
  MapPin,
  Plus,
  RotateCcw,
  Play,
  Pause,
  CheckCircle,
  Clock,
  DollarSign,
  Users,
  Building,
  Layers
} from 'lucide-react';

// Simulator types
interface SimulatorVehicle {
  id: string;
  type: 'motorcycle' | 'car' | 'truck';
  licensePlate: string;
  entryTime: Date;
  spotId?: string;
  isParked: boolean;
}

interface SimulatorSpot {
  id: string;
  level: number;
  row: number;
  spotNumber: number;
  size: 1 | 2 | 4; // 1=motorcycle, 2=car, 4=truck
  isOccupied: boolean;
  vehicleId?: string;
  isReserved?: boolean;
  reservationType?: 'handicapped' | 'vip' | 'electric';
}

interface SimulatorLevel {
  number: number;
  spots: SimulatorSpot[];
  totalSpots: number;
  occupiedSpots: number;
}

interface ParkingStats {
  totalVehicles: number;
  parkedVehicles: number;
  waitingVehicles: number;
  totalRevenue: number;
  averageParkingTime: number;
  occupancyRate: number;
}

const ParkingLotSimulator: React.FC = () => {
  const [levels, setLevels] = useState<SimulatorLevel[]>([]);
  const [vehicles, setVehicles] = useState<SimulatorVehicle[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [stats, setStats] = useState<ParkingStats>({
    totalVehicles: 0,
    parkedVehicles: 0,
    waitingVehicles: 0,
    totalRevenue: 0,
    averageParkingTime: 0,
    occupancyRate: 0
  });
  const [selectedSpot, setSelectedSpot] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<string[]>([]);

  const addNotification = useCallback((message: string) => {
    setNotifications(prev => [...prev.slice(-4), message]);
    setTimeout(() => {
      setNotifications(prev => prev.slice(1));
    }, 5000);
  }, []);

  const initializeParkingLot = useCallback(() => {
    const newLevels: SimulatorLevel[] = [];
    
      // Create 3 levels with different configurations
      for (let level = 1; level <= 3; level++) {
        const spots: SimulatorSpot[] = [];
        
        // Each level has 5 rows with 8 spots each
        for (let row = 1; row <= 5; row++) {
          for (let spot = 1; spot <= 8; spot++) {
            let size: 1 | 2 | 4 = 2; // Default car size
            let reservationType: 'handicapped' | 'vip' | 'electric' | undefined;
            
            // First row: mix of motorcycle and car spots
            if (row === 1) {
              size = spot <= 4 ? 1 : 2;
            }
            // Last row: truck spots
            else if (row === 5) {
              size = 4;
            }
            // Add some reserved spots
            else if (row === 2 && spot <= 2) {
              reservationType = 'handicapped';
            }
            else if (row === 2 && spot >= 7) {
              reservationType = 'vip';
            }
            
            spots.push({
              id: `L${level}-R${row}-S${spot}`,
              level,
              row,
              spotNumber: spot,
              size,
              isOccupied: false,
              isReserved: !!reservationType,
              reservationType
            });
          }
        }
      
      newLevels.push({
        number: level,
        spots,
        totalSpots: spots.length,
        occupiedSpots: 0
      });
    }
    
    setLevels(newLevels);
    addNotification('Parking lot initialized with 3 levels, 120 total spots');
  }, [addNotification]);

  // Initialize parking lot with sample data
  useEffect(() => {
    initializeParkingLot();
  }, [initializeParkingLot]);

  const generateRandomVehicle = (): SimulatorVehicle => {
    const weights = { motorcycle: 0.3, car: 0.6, truck: 0.1 };
    
    // Weighted random selection
    const rand = Math.random();
    let selectedType: 'motorcycle' | 'car' | 'truck' = 'car';
    if (rand < weights.motorcycle) selectedType = 'motorcycle';
    else if (rand < weights.motorcycle + weights.car) selectedType = 'car';
    else selectedType = 'truck';
    
    const licensePlate = `${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${
      String.fromCharCode(65 + Math.floor(Math.random() * 26))
    }${Math.floor(Math.random() * 9000) + 1000}`;
    
    return {
      id: `vehicle-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      type: selectedType,
      licensePlate,
      entryTime: new Date(),
      isParked: false
    };
  };

  const findAvailableSpot = useCallback((vehicleType: 'motorcycle' | 'car' | 'truck'): SimulatorSpot | null => {
    const requiredSize = vehicleType === 'motorcycle' ? 1 : vehicleType === 'car' ? 2 : 4;
    
    // Search through all levels
    for (const level of levels) {
      for (const spot of level.spots) {
        if (!spot.isOccupied && spot.size >= requiredSize && !spot.isReserved) {
          return spot;
        }
      }
    }
    
    return null;
  }, [levels]);

  const parkVehicle = useCallback((vehicle: SimulatorVehicle): boolean => {
    const availableSpot = findAvailableSpot(vehicle.type);
    
    if (!availableSpot) {
      addNotification(`❌ No available spot for ${vehicle.type} ${vehicle.licensePlate}`);
      return false;
    }
    
    // Update spot
    setLevels(prevLevels => 
      prevLevels.map(level => ({
        ...level,
        spots: level.spots.map(spot => 
          spot.id === availableSpot.id 
            ? { ...spot, isOccupied: true, vehicleId: vehicle.id }
            : spot
        ),
        occupiedSpots: level.number === availableSpot.level 
          ? level.occupiedSpots + 1 
          : level.occupiedSpots
      }))
    );
    
    // Update vehicle
    setVehicles(prevVehicles => 
      prevVehicles.map(v => 
        v.id === vehicle.id 
          ? { ...v, isParked: true, spotId: availableSpot.id }
          : v
      )
    );
    
    addNotification(`✅ ${vehicle.type} ${vehicle.licensePlate} parked at ${availableSpot.id}`);
    return true;
  }, [findAvailableSpot, addNotification]);

  const removeVehicle = useCallback((vehicleId: string): void => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (!vehicle || !vehicle.spotId) return;
    
    // Calculate parking time and fee
    const parkingTime = Date.now() - vehicle.entryTime.getTime();
    const hours = Math.ceil(parkingTime / (1000 * 60 * 60));
    const fee = vehicle.type === 'motorcycle' ? hours * 5 : 
                vehicle.type === 'car' ? hours * 10 : hours * 20;
    
    // Update spot
    setLevels(prevLevels => 
      prevLevels.map(level => ({
        ...level,
        spots: level.spots.map(spot => 
          spot.id === vehicle.spotId 
            ? { ...spot, isOccupied: false, vehicleId: undefined }
            : spot
        ),
        occupiedSpots: level.spots.some(s => s.id === vehicle.spotId && s.level === level.number)
          ? level.occupiedSpots - 1 
          : level.occupiedSpots
      }))
    );
    
    // Remove vehicle
    setVehicles(prevVehicles => prevVehicles.filter(v => v.id !== vehicleId));
    
    // Update stats
    setStats(prevStats => ({
      ...prevStats,
      totalRevenue: prevStats.totalRevenue + fee
    }));
    
    addNotification(`🚗 ${vehicle.licensePlate} left. Fee: $${fee} (${hours}h)`);
  }, [vehicles, addNotification]);

  const addRandomVehicle = useCallback(() => {
    const newVehicle = generateRandomVehicle();
    setVehicles(prev => [...prev, newVehicle]);
    
    // Try to park immediately
    setTimeout(() => {
      parkVehicle(newVehicle);
    }, 500);
  }, [parkVehicle]);

  const simulationTick = useCallback(() => {
    if (!isRunning) return;
    
    // Random chance to add new vehicle
    if (Math.random() < 0.3) {
      addRandomVehicle();
    }
    
    // Random chance to remove parked vehicles
    const parkedVehicles = vehicles.filter(v => v.isParked);
    if (parkedVehicles.length > 0 && Math.random() < 0.2) {
      const randomVehicle = parkedVehicles[Math.floor(Math.random() * parkedVehicles.length)];
      removeVehicle(randomVehicle.id);
    }
    
    // Update stats
    const totalSpots = levels.reduce((sum, level) => sum + level.totalSpots, 0);
    const occupiedSpots = levels.reduce((sum, level) => sum + level.occupiedSpots, 0);
    
    setStats(prevStats => ({
      ...prevStats,
      totalVehicles: vehicles.length,
      parkedVehicles: vehicles.filter(v => v.isParked).length,
      waitingVehicles: vehicles.filter(v => !v.isParked).length,
      occupancyRate: totalSpots > 0 ? (occupiedSpots / totalSpots) * 100 : 0
    }));
  }, [isRunning, vehicles, levels, addRandomVehicle, removeVehicle]);

  // Simulation loop
  useEffect(() => {
    if (!isRunning) return;
    
    const interval = setInterval(simulationTick, 2000 / speed);
    return () => clearInterval(interval);
  }, [simulationTick, speed, isRunning]);

  const getSpotIcon = (spot: SimulatorSpot) => {
    if (spot.isOccupied) {
      const vehicle = vehicles.find(v => v.id === spot.vehicleId);
      if (vehicle) {
        switch (vehicle.type) {
          case 'motorcycle': return <Bike className="w-4 h-4 text-blue-600" />;
          case 'car': return <Car className="w-4 h-4 text-green-600" />;
          case 'truck': return <Truck className="w-4 h-4 text-red-600" />;
        }
      }
    }
    return <MapPin className={`w-4 h-4 ${spot.isReserved ? 'text-purple-400' : 'text-gray-400'}`} />;
  };

  const getSpotColor = (spot: SimulatorSpot) => {
    if (selectedSpot === spot.id) return 'bg-yellow-200 dark:bg-yellow-800 border-yellow-400';
    if (spot.isOccupied) return 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700';
    if (spot.isReserved) return 'bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700';
    return 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700';
  };

  const resetSimulation = () => {
    setIsRunning(false);
    setVehicles([]);
    setStats({
      totalVehicles: 0,
      parkedVehicles: 0,
      waitingVehicles: 0,
      totalRevenue: 0,
      averageParkingTime: 0,
      occupancyRate: 0
    });
    initializeParkingLot();
    setNotifications([]);
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Simulation Controls</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsRunning(!isRunning)}
              className={`p-2 rounded-lg transition-colors ${
                isRunning 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <button
              onClick={resetSimulation}
              className="p-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Speed: {speed}x
            </label>
            <input
              type="range"
              min="0.5"
              max="3"
              step="0.5"
              value={speed}
              onChange={(e) => setSpeed(parseFloat(e.target.value))}
              className="w-full"
            />
          </div>
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={addRandomVehicle}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Vehicle</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
          <div className="flex items-center space-x-2 mb-2">
            <Users className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-800 dark:text-blue-300">Total Vehicles</span>
          </div>
          <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{stats.totalVehicles}</div>
        </div>
        
        <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg border border-green-200 dark:border-green-700">
          <div className="flex items-center space-x-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-800 dark:text-green-300">Parked</span>
          </div>
          <div className="text-2xl font-bold text-green-900 dark:text-green-100">{stats.parkedVehicles}</div>
        </div>
        
        <div className="bg-yellow-50 dark:bg-yellow-900/30 p-4 rounded-lg border border-yellow-200 dark:border-yellow-700">
          <div className="flex items-center space-x-2 mb-2">
            <Clock className="w-5 h-5 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Occupancy</span>
          </div>
          <div className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
            {stats.occupancyRate.toFixed(1)}%
          </div>
        </div>
        
        <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-lg border border-purple-200 dark:border-purple-700">
          <div className="flex items-center space-x-2 mb-2">
            <DollarSign className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-800 dark:text-purple-300">Revenue</span>
          </div>
          <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
            ${stats.totalRevenue}
          </div>
        </div>
      </div>

      {/* Parking Lot Visualization */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
          <Building className="w-5 h-5 mr-2 text-gray-600" />
          Parking Lot Levels
        </h3>
        
        <div className="space-y-6">
          {levels.map((level) => (
            <div key={level.number} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <Layers className="w-4 h-4 mr-2" />
                  Level {level.number}
                </h4>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {level.occupiedSpots}/{level.totalSpots} occupied
                </span>
              </div>
              
              <div className="grid grid-cols-8 gap-1">
                {level.spots.map((spot) => (
                  <button
                    key={spot.id}
                    onClick={() => setSelectedSpot(selectedSpot === spot.id ? null : spot.id)}
                    className={`relative aspect-square rounded border-2 transition-all hover:scale-105 ${getSpotColor(spot)}`}
                    title={`${spot.id} - Size: ${spot.size} - ${spot.isOccupied ? 'Occupied' : 'Available'}${spot.isReserved ? ` (${spot.reservationType})` : ''}`}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      {getSpotIcon(spot)}
                    </div>
                    {spot.isReserved && (
                      <div className="absolute top-0 right-0 w-2 h-2 bg-purple-500 rounded-full"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Legend</h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-100 dark:bg-green-900/30 border-2 border-green-300 dark:border-green-700 rounded"></div>
            <span className="text-gray-700 dark:text-gray-300">Available</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-100 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded"></div>
            <span className="text-gray-700 dark:text-gray-300">Occupied</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-purple-100 dark:bg-purple-900/30 border-2 border-purple-300 dark:border-purple-700 rounded"></div>
            <span className="text-gray-700 dark:text-gray-300">Reserved</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-200 dark:bg-yellow-800 border-2 border-yellow-400 rounded"></div>
            <span className="text-gray-700 dark:text-gray-300">Selected</span>
          </div>
        </div>
        
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <Bike className="w-4 h-4 text-blue-600" />
            <span className="text-gray-700 dark:text-gray-300">Motorcycle (Size 1)</span>
          </div>
          <div className="flex items-center space-x-2">
            <Car className="w-4 h-4 text-green-600" />
            <span className="text-gray-700 dark:text-gray-300">Car (Size 2)</span>
          </div>
          <div className="flex items-center space-x-2">
            <Truck className="w-4 h-4 text-red-600" />
            <span className="text-gray-700 dark:text-gray-300">Truck (Size 4)</span>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">Recent Activity</h4>
          <div className="space-y-1">
            {notifications.map((notification, index) => (
              <div
                key={index}
                className="text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 p-2 rounded border border-blue-200 dark:border-blue-700"
              >
                {notification}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Spot Details */}
      {selectedSpot && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Spot Details</h4>
          {(() => {
            const spot = levels.flatMap(l => l.spots).find(s => s.id === selectedSpot);
            const vehicle = spot?.vehicleId ? vehicles.find(v => v.id === spot.vehicleId) : null;
            
            if (!spot) return null;
            
            return (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong className="text-gray-700 dark:text-gray-300">Spot ID:</strong>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{spot.id}</span>
                </div>
                <div>
                  <strong className="text-gray-700 dark:text-gray-300">Size:</strong>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{spot.size} units</span>
                </div>
                <div>
                  <strong className="text-gray-700 dark:text-gray-300">Status:</strong>
                  <span className={`ml-2 ${spot.isOccupied ? 'text-red-600' : 'text-green-600'}`}>
                    {spot.isOccupied ? 'Occupied' : 'Available'}
                  </span>
                </div>
                {spot.isReserved && (
                  <div>
                    <strong className="text-gray-700 dark:text-gray-300">Reserved:</strong>
                    <span className="ml-2 text-purple-600 dark:text-purple-400">
                      {spot.reservationType}
                    </span>
                  </div>
                )}
                {vehicle && (
                  <>
                    <div className="col-span-2">
                      <strong className="text-gray-700 dark:text-gray-300">Vehicle:</strong>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">
                        {vehicle.type} - {vehicle.licensePlate}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <strong className="text-gray-700 dark:text-gray-300">Parked Since:</strong>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">
                        {vehicle.entryTime.toLocaleTimeString()}
                      </span>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default ParkingLotSimulator;
